import posthog from "posthog-js";
import { WidgetConfig } from "../store/types";

export type WidgetTelemetryScalar = string | number | boolean | null | undefined;
export type WidgetTelemetryProperties = Record<
  string,
  WidgetTelemetryScalar | WidgetTelemetryScalar[]
>;

declare global {
  interface Window {
    REMSTAL_POSTHOG_KEY?: string;
    REMSTAL_POSTHOG_HOST?: string;
  }
}

const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";
const MAX_PENDING_EVENTS = 20;
const SENSITIVE_KEY_PATTERN =
  /(token|secret|password|authorization|cookie|signed_url|api[_-]?key|message|transcript|prompt|input)/i;

let initialized = false;
let initStarted = false;
let relayApiUrl: string | undefined;
let pendingEvents: { event: string; properties: WidgetTelemetryProperties }[] = [];

type RuntimeTelemetryConfig = {
  enabled?: boolean;
  posthogKey?: unknown;
  posthogHost?: unknown;
};

const scriptOrigin = (() => {
  if (typeof document === "undefined") return undefined;

  const currentScript = document.currentScript as HTMLScriptElement | null;
  const scriptSrc =
    currentScript?.src ||
    Array.from(document.querySelectorAll("script"))
      .map((script) => (script as HTMLScriptElement).src)
      .find((src) => /\/widget\.js(?:\?|$)/.test(src));

  if (!scriptSrc) return undefined;

  try {
    return new URL(scriptSrc).origin;
  } catch {
    return undefined;
  }
})();

function isTelemetryScalar(value: unknown): value is WidgetTelemetryScalar {
  return (
    value === null ||
    value === undefined ||
    ["string", "number", "boolean"].includes(typeof value)
  );
}

function isTelemetryValue(
  value: unknown
): value is WidgetTelemetryScalar | WidgetTelemetryScalar[] {
  return isTelemetryScalar(value) ||
    (Array.isArray(value) && value.every(isTelemetryScalar));
}

function getPostHogKey(): string | undefined {
  if (typeof window !== "undefined" && window.REMSTAL_POSTHOG_KEY) {
    return window.REMSTAL_POSTHOG_KEY;
  }

  return process.env.WIDGET_POSTHOG_KEY || undefined;
}

function getPostHogHost(): string {
  if (typeof window !== "undefined" && window.REMSTAL_POSTHOG_HOST) {
    return window.REMSTAL_POSTHOG_HOST;
  }

  return process.env.WIDGET_POSTHOG_HOST || DEFAULT_POSTHOG_HOST;
}

function getTelemetryApiUrl(): string | undefined {
  if (typeof window !== "undefined" && (window as any).REMSTAL_API_URL) {
    return (window as any).REMSTAL_API_URL;
  }

  if (process.env.WIDGET_API_URL) {
    return process.env.WIDGET_API_URL;
  }

  return scriptOrigin;
}

function getDistinctId(): string {
  const storageKey = "remstal_widget_distinct_id";
  if (typeof window === "undefined") return "server-render";

  try {
    const existing = window.localStorage.getItem(storageKey);
    if (existing) return existing;

    const generated =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `widget-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(storageKey, generated);
    return generated;
  } catch {
    return `widget-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function sanitizeWidgetTelemetryProperties(
  properties: Record<string, unknown> = {}
): WidgetTelemetryProperties {
  const sanitized: WidgetTelemetryProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) continue;
    if (value === undefined || !isTelemetryValue(value)) continue;
    sanitized[key] = value;
  }
  return sanitized;
}

export function errorWidgetTelemetryProperties(
  error: unknown
): WidgetTelemetryProperties {
  if (error instanceof Error) {
    return {
      error_name: error.name,
      error_message: error.message,
    };
  }

  return {
    error_name: "UnknownError",
    error_message: String(error),
  };
}

function startPostHog(
  key: string,
  host: string,
  config: WidgetConfig
): boolean {
  if (initialized || typeof window === "undefined") {
    return initialized;
  }

  posthog.init(key, {
    api_host: host,
    capture_pageview: false,
    autocapture: {
      dom_event_allowlist: ["click", "change"],
      element_allowlist: ["button", "a", "form", "input", "textarea"],
    },
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: ".pii,[data-private]",
      compress_events: true,
    },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    },
    before_send: (event) => {
      if (!event) return event;
      if (event.properties) {
        event.properties = sanitizeWidgetTelemetryProperties(event.properties);
      }
      return event;
    },
  });

  initialized = true;
  captureWidgetEvent("widget_loaded", {
    widget_id: config.widgetId || config.agentId || "unknown",
    mode: config.mode || "combined",
    ui_variant: config.uiVariant || "legacy",
    current_url: window.location.href,
    host: window.location.host,
    pathname: window.location.pathname,
  });

  const queued = pendingEvents;
  pendingEvents = [];
  queued.forEach(({ event, properties }) => {
    captureWidgetEvent(event, properties);
  });

  return true;
}

async function loadRuntimeTelemetryConfig(apiUrl: string): Promise<RuntimeTelemetryConfig | null> {
  try {
    const response = await fetch(`${apiUrl}/api/widget/telemetry`, {
      credentials: "omit",
    });
    if (!response.ok) return null;
    return (await response.json()) as RuntimeTelemetryConfig;
  } catch {
    return null;
  }
}

function relayWidgetEvent(
  event: string,
  properties: WidgetTelemetryProperties
): void {
  if (!relayApiUrl || typeof fetch !== "function") return;

  const payload = JSON.stringify({
    event,
    distinctId: getDistinctId(),
    properties,
  });

  void fetch(`${relayApiUrl}/api/widget/telemetry/capture`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    keepalive: payload.length < 60_000,
    body: payload,
  }).catch(() => undefined);
}

function initWidgetTelemetryFromRuntime(config: WidgetConfig): void {
  const apiUrl = getTelemetryApiUrl();
  if (!apiUrl) return;

  initStarted = true;
  void loadRuntimeTelemetryConfig(apiUrl).then((runtimeConfig) => {
    if (!runtimeConfig?.enabled) return;
    relayApiUrl = apiUrl;
    if (typeof runtimeConfig.posthogKey !== "string") return;
    const host = typeof runtimeConfig.posthogHost === "string"
      ? runtimeConfig.posthogHost
      : DEFAULT_POSTHOG_HOST;

    startPostHog(runtimeConfig.posthogKey, host, config);
  });
}

export function initWidgetTelemetry(config: WidgetConfig): boolean {
  if (initialized || initStarted || typeof window === "undefined") {
    return initialized;
  }

  const key = getPostHogKey();
  if (key) {
    initStarted = true;
    return startPostHog(key, getPostHogHost(), config);
  }

  initWidgetTelemetryFromRuntime(config);
  return false;
}

export function captureWidgetEvent(
  event: string,
  properties: WidgetTelemetryProperties = {}
): void {
  const sanitizedProperties = sanitizeWidgetTelemetryProperties({
    app: "remstal-widget",
    ...properties,
  });

  if (!initialized) {
    if (initStarted && pendingEvents.length < MAX_PENDING_EVENTS) {
      pendingEvents.push({ event, properties: sanitizedProperties });
    }
    return;
  }

  if (relayApiUrl) {
    relayWidgetEvent(event, sanitizedProperties);
    return;
  }

  posthog.capture(event, sanitizedProperties);
}

export function resetWidgetTelemetryForTests(): void {
  initialized = false;
  initStarted = false;
  relayApiUrl = undefined;
  pendingEvents = [];
}
